/**
 * Older-message merge helper.
 *
 * The rendered message list is owned by `useChat`, which seeds from
 * `initialMessages` ONCE at mount. Older history fetched later (via the
 * infinite-scroll query) arrives on the `initialMessages` prop but never reaches
 * `useChat`'s state on its own. To make reverse pagination work we prepend the
 * newly-arrived older messages into `useChat` via `setMessages`.
 *
 * This helper computes exactly which server messages are not yet in the live
 * list, keyed by `id`. Dedup-by-id makes the merge safe regardless of how the
 * API orders its pages: already-present messages (the current page, plus any
 * live messages the user just sent) are never duplicated, and only genuinely new
 * history is returned.
 */

import type { UIMessage } from 'ai';

/**
 * Returns the server messages that are absent from the current live list, in
 * server order. Callers prepend these to the live list (older history precedes
 * everything already loaded) and decrement Virtuoso's `firstItemIndex` by the
 * returned length to keep the viewport anchored.
 */
export function computeOlderMessagesToPrepend(
  serverMessages: readonly UIMessage[] | undefined,
  currentMessages: readonly UIMessage[] | undefined
): UIMessage[] {
  if (!serverMessages || serverMessages.length === 0) return [];
  const currentIds = new Set((currentMessages ?? []).map((m) => m.id));
  return serverMessages.filter((m) => m.id != null && !currentIds.has(m.id));
}
